<script setup lang="ts">
import { useQueryClient } from '@tanstack/vue-query';
import { computed } from 'vue';
import compromissosAtrasados from '~/graphql/pre-defined-filters/compromissosAtrasados';
import compromissosConcluidos from '~/graphql/pre-defined-filters/compromissosConcluidos';
import compromissosVencimentoHoje from '~/graphql/pre-defined-filters/compromissosVencimentoHoje';
import compromissosVencimentoProximosDias from '~/graphql/pre-defined-filters/compromissosVencimentoProximosDias';
import pastasAcordosNoMes from '~/graphql/pre-defined-filters/pastasAcordosNoMes';
import pastasAtivas from '~/graphql/pre-defined-filters/pastasAtivas';
import pastasGcpjDesatualizado from '~/graphql/pre-defined-filters/pastasGcpjDesatualizado';
import publicacoesHoje from '~/graphql/pre-defined-filters/publicacoesHoje';
import PERFIL_ACESSO from '~/perfilAcesso';
import { formatDinheiro } from '~/shared/utils/helpers';
import { useDatatableDrawerStore } from '~/stores/datatableStore';

interface Data {
  key: string;
  value: any;
}

interface Props {
  gerencia: string;
  data: Data[];
  showCompromissos?: boolean;
  showPastas?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  showPastas: true,
  showCompromissos: true,
});

const datatableDrawerStore = useDatatableDrawerStore();

// PASTAS ATIVAS ULTIMOS MESES
const pastasAtivasUltimosMeses = computed(() => {
  return Object.entries(
    props.data.find(
      (x) => x.key === `${props.gerencia}-totalPastasAtivasUltimosMeses`,
    )?.value ?? [],
  ).sort(([keyA, _], [keyB, __]) => {
    const a = Number(keyA.split('|')[1]);
    const b = Number(keyB.split('|')[1]);
    return a - b;
  });
});

const pastasAtivasUltimosMesesCategories = computed(() => {
  return pastasAtivasUltimosMeses.value.map(([x, _]) => x.split('|')[0]);
});

const pastasAtivasUltimosMesesSeries = computed(() => {
  return [
    {
      name: 'Total de pastas',
      data: pastasAtivasUltimosMeses.value.map(([_, x]) => x as number),
    },
  ];
});

// VALORES CAUSAS ULTIMOS MESES
const valoresCausasUltimosMeses = computed(() => {
  return Object.entries(
    props.data.find(
      (x) => x.key === `${props.gerencia}-totalValoresCausasUltimosMeses`,
    )?.value ?? [],
  ).sort(([keyA, _], [keyB, __]) => {
    const a = Number(keyA.split('|')[1]);
    const b = Number(keyB.split('|')[1]);
    return a - b;
  });
});

const valoresCausasUltimosMesesCategories = computed(() => {
  return valoresCausasUltimosMeses.value.map(([x, _]) => x.split('|')[0]);
});

const valoresCausasUltimosMesesSeries = computed(() => {
  return [
    {
      name: 'Valores das causas',
      data: valoresCausasUltimosMeses.value.map(([_, x]) => x as number),
    },
  ];
});

// TOTAL ACORDOS ULTIMOS MESES
const totalAcordosUltimosMeses = computed(() => {
  return Object.entries(
    props.data.find(
      (x) => x.key === `${props.gerencia}-totalAcordosUltimosMeses`,
    )?.value ?? [],
  ).sort(([keyA, _], [keyB, __]) => {
    const a = Number(keyA.split('|')[1]);
    const b = Number(keyB.split('|')[1]);
    return a - b;
  });
});

const totalAcordosUltimosMesesCategories = computed(() => {
  return totalAcordosUltimosMeses.value.map(([x, _]) => x.split('|')[0]);
});

const totalAcordosUltimosMesesSeries = computed(() => {
  return [
    {
      name: 'Total de acordos',
      data: totalAcordosUltimosMeses.value.map(([_, x]) => x as number),
    },
  ];
});

// TOTAL PUBLICACOES ULTIMOS DIAS
const totalPublicacoesUltimosDias = computed(() => {
  return Object.entries(
    props.data.find(
      (x) => x.key === `${props.gerencia}-totalPublicacoesUltimosDias`,
    )?.value ?? [],
  ).sort(([keyA, _], [keyB, __]) => {
    const a = Number(keyA.split('|')[1]);
    const b = Number(keyB.split('|')[1]);
    return a - b;
  });
});

const totalPublicacoesUltimosDiasCategories = computed(() => {
  return totalPublicacoesUltimosDias.value.map(([x, _]) => x.split('|')[0]);
});

const totalPublicacoesUltimosDiasSeries = computed(() => {
  return [
    {
      name: 'Total de publicações',
      data: totalPublicacoesUltimosDias.value.map(([_, x]) => x as number),
    },
  ];
});

// TOTAL COMPROMISSOS ATRASADOS POR RESPONSAVEL
const totalCompromissosAtrasadosAgrupadosPorResponsavel = computed(() => {
  return Object.entries(
    props.data.find(
      (x) =>
        x.key ===
        `${props.gerencia}-totalCompromissosAtrasadosAgrupadosPorResponsavel`,
    )?.value ?? [],
  ).sort(([_, valA], [__, valB]) => {
    return (valB as number) - (valA as number);
  });
});

const compromissosPorUsuariosCategories = computed(() => {
  return totalCompromissosAtrasadosAgrupadosPorResponsavel.value.map(
    ([x, _]) => x,
  );
});

const totalCompromissosAtrasadosAgrupadosPorResponsavelSerie = computed(() => {
  const dict =
    (props.data.find(
      (x) =>
        x.key ===
        `${props.gerencia}-totalCompromissosAtrasadosAgrupadosPorResponsavel`,
    )?.value as Record<string, number>) ?? {};
  const arr: number[] = [];

  compromissosPorUsuariosCategories.value.forEach((x) => {
    if (x in dict) {
      arr.push(dict[x]);
    } else {
      arr.push(0);
    }
  });

  return {
    name: 'Total de compromissos atrasados',
    data: arr,
  };
});

const totalCompromissosVencimentoProximosDiasAgrupadosPorResponsavelSerie =
  computed(() => {
    const dict =
      (props.data.find(
        (x) =>
          x.key ===
          `${props.gerencia}-totalCompromissosVencimentoProximosDiasAgrupadosPorResponsavel`,
      )?.value as Record<string, number>) ?? {};
    const arr: number[] = [];

    compromissosPorUsuariosCategories.value.forEach((x) => {
      if (x in dict) {
        arr.push(dict[x]);
      } else {
        arr.push(0);
      }
    });

    return {
      name: 'Total de compromissos com vencimento nos próximos dias',
      data: arr,
    };
  });

const totalCompromissosVencimentoHojeAgrupadosPorResponsavelSerie = computed(
  () => {
    const dict =
      (props.data.find(
        (x) =>
          x.key ===
          `${props.gerencia}-totalCompromissosVencimentoHojeAgrupadosPorResponsavel`,
      )?.value as Record<string, number>) ?? {};
    const arr: number[] = [];

    compromissosPorUsuariosCategories.value.forEach((x) => {
      if (x in dict) {
        arr.push(dict[x]);
      } else {
        arr.push(0);
      }
    });

    return {
      name: 'Total de compromissos com vencimento hoje',
      data: arr,
    };
  },
);

// PASTAS POR STATUS
const pastasAgrupadasPorStatus = computed(() => {
  return Object.entries(
    props.data.find(
      (x) => x.key === `${props.gerencia}-totalPastasAgrupadasPorStatus`,
    )?.value ?? [],
  ).sort(([_, valA], [__, valB]) => {
    return (valB as number) - (valA as number);
  });
});

// PASTAS POR STATUS
const totalPastasAtivasAgrupadosPorResponsavel = computed(() => {
  return Object.entries(
    props.data.find(
      (x) =>
        x.key === `${props.gerencia}-totalPastasAtivasAgrupadosPorResponsavel`,
    )?.value ?? [],
  ).sort(([_, valA], [__, valB]) => {
    return (valB as number) - (valA as number);
  });
});

const queryClient = useQueryClient();

function onDatatableDrawerClose() {
  queryClient.invalidateQueries({
    queryKey: [props.gerencia],
    exact: true,
  });
}
</script>
<template>
  <div v-if="props.showCompromissos">
    <h2 style="margin-top: 12px">Compromissos</h2>

    <a-row :gutter="24" style="margin-top: 24px">
      <a-col :span="6">
        <Stats
          title="EM ATRASO"
          :clickable="true"
          :value="
            props.data.find(
              (x) => x.key === `${props.gerencia}-totalCompromissosEmAtraso`,
            )?.value
          "
          @click="
            () =>
              datatableDrawerStore.open({
                componentName: 'CompromissoGqlDatatable',
                title: 'COMPROMISSOS EM ATRASO',
                variables: compromissosAtrasados({
                  gerencia:
                    PERFIL_ACESSO[props.gerencia as keyof typeof PERFIL_ACESSO],
                }),
                onClose: onDatatableDrawerClose,
              })
          "
        ></Stats>
      </a-col>
      <a-col :span="6">
        <Stats
          title="VENCIMENTO HOJE"
          :clickable="true"
          :value="
            props.data.find(
              (x) =>
                x.key === `${props.gerencia}-totalCompromissosVencimentoHoje`,
            )?.value
          "
          @click="
            () =>
              datatableDrawerStore.open({
                componentName: 'CompromissoGqlDatatable',
                title: 'COMPROMISSOS COM VENCIMENTO HOJE',
                variables: compromissosVencimentoHoje({
                  gerencia:
                    PERFIL_ACESSO[props.gerencia as keyof typeof PERFIL_ACESSO],
                }),
                onClose: onDatatableDrawerClose,
              })
          "
        ></Stats>
      </a-col>
      <a-col :span="6">
        <Stats
          title="VENCIMENTO PRÓXIMOS DIAS"
          :clickable="true"
          :value="
            props.data.find(
              (x) =>
                x.key ===
                `${props.gerencia}-totalCompromissosVencimentoProximosDias`,
            )?.value
          "
          @click="
            () =>
              datatableDrawerStore.open({
                componentName: 'CompromissoGqlDatatable',
                title: 'COMPROMISSOS COM VENCIMENTO NOS PRÓXIMOS DIAS',
                variables: compromissosVencimentoProximosDias({
                  gerencia:
                    PERFIL_ACESSO[props.gerencia as keyof typeof PERFIL_ACESSO],
                }),
                onClose: onDatatableDrawerClose,
              })
          "
        ></Stats>
      </a-col>
      <a-col :span="6">
        <Stats
          title="CONCLUÍDOS"
          :clickable="true"
          :value="
            props.data.find(
              (x) => x.key === `${props.gerencia}-totalCompromissosConcluidos`,
            )?.value
          "
          @click="
            () =>
              datatableDrawerStore.open({
                componentName: 'CompromissoGqlDatatable',
                title: 'COMPROMISSOS CONCLUÍDOS',
                variables: compromissosConcluidos({
                  gerencia:
                    PERFIL_ACESSO[props.gerencia as keyof typeof PERFIL_ACESSO],
                }),
                onClose: onDatatableDrawerClose,
              })
          "
        ></Stats>
      </a-col>
    </a-row>

    <a-row :gutter="24" style="margin-top: 24px">
      <a-col :span="24">
        <StackedBarChart
          title="USUÁRIOS / COMPROMISSOS"
          :height="350"
          :categories="compromissosPorUsuariosCategories"
          :series="[
            totalCompromissosAtrasadosAgrupadosPorResponsavelSerie,
            totalCompromissosVencimentoProximosDiasAgrupadosPorResponsavelSerie,
            totalCompromissosVencimentoHojeAgrupadosPorResponsavelSerie,
          ]"
        />
      </a-col>
    </a-row>
  </div>
  <div v-if="props.showPastas">
    <h2 style="margin-top: 24px">Pastas</h2>

    <a-row :gutter="24" style="margin-top: 24px">
      <a-col :span="6">
        <Stats
          title="PASTAS ATIVAS"
          :clickable="true"
          :value="
            props.data.find(
              (x) => x.key === `${props.gerencia}-totalPastasAtivas`,
            )?.value
          "
          @click="
            () =>
              datatableDrawerStore.open({
                componentName: 'PastaGqlDatatable',
                title: 'PASTAS ATIVAS',
                variables: pastasAtivas({
                  gerencia:
                    PERFIL_ACESSO[props.gerencia as keyof typeof PERFIL_ACESSO],
                }),
                onClose: onDatatableDrawerClose,
              })
          "
        ></Stats>
      </a-col>
      <a-col :span="6">
        <Stats
          title="GCPJ DESATUALIZADO"
          :clickable="true"
          :value="
            props.data.find(
              (x) =>
                x.key === `${props.gerencia}-totalPastasGcpjsDesatualizados`,
            )?.value
          "
          @click="
            () =>
              datatableDrawerStore.open({
                componentName: 'PastaGqlDatatable',
                title: 'PASTAS COM GCPJ DESATUALIZADO',
                variables: pastasGcpjDesatualizado({
                  gerencia:
                    PERFIL_ACESSO[props.gerencia as keyof typeof PERFIL_ACESSO],
                }),
                onClose: onDatatableDrawerClose,
              })
          "
        ></Stats>
      </a-col>
      <a-col :span="6">
        <Stats
          title="ACORDOS NO MÊS"
          :clickable="true"
          :value="
            props.data.find(
              (x) => x.key === `${props.gerencia}-totalAcordosNoMes`,
            )?.value
          "
          @click="
            () =>
              datatableDrawerStore.open({
                componentName: 'PastaGqlDatatable',
                title: 'PASTAS COM ACORDOS NO MÊS',
                variables: pastasAcordosNoMes({
                  gerencia:
                    PERFIL_ACESSO[props.gerencia as keyof typeof PERFIL_ACESSO],
                }),
                onClose: onDatatableDrawerClose,
              })
          "
        ></Stats>
      </a-col>
      <a-col :span="6">
        <Stats
          title="PUBLICAÇÕES HOJE"
          :clickable="true"
          :value="
            props.data.find(
              (x) => x.key === `${props.gerencia}-totalPublicacoesHoje`,
            )?.value
          "
          @click="
            () =>
              datatableDrawerStore.open({
                componentName: 'PublicacaoNumeroProcessoGqlDatatable',
                title: 'PUBLICAÇÕES HOJE',
                variables: publicacoesHoje({
                  gerencia:
                    PERFIL_ACESSO[props.gerencia as keyof typeof PERFIL_ACESSO],
                }),
                onClose: onDatatableDrawerClose,
              })
          "
        ></Stats>
      </a-col>
    </a-row>

    <a-row :gutter="24" style="margin-top: 24px">
      <a-col :span="12">
        <LineChart
          title="PASTAS ATIVAS"
          :categories="pastasAtivasUltimosMesesCategories"
          :series="pastasAtivasUltimosMesesSeries"
        />
      </a-col>
      <a-col :span="12">
        <LineChart
          title="VALORES CAUSAS"
          :categories="valoresCausasUltimosMesesCategories"
          :series="valoresCausasUltimosMesesSeries"
          :value-formater="(value: any) => formatDinheiro(value)"
        />
      </a-col>
    </a-row>

    <a-row :gutter="24" style="margin-top: 24px">
      <a-col :span="12">
        <Stats
          title="VALORES CAUSAS"
          :value="
            formatDinheiro(
              props.data.find(
                (x) => x.key === `${props.gerencia}-totalValoresCausas`,
              )?.value,
            )
          "
        />
      </a-col>
      <a-col :span="12">
        <Stats
          title="VALOR ACORDO NO MÊS"
          :value="
            formatDinheiro(
              props.data.find(
                (x) => x.key === `${props.gerencia}-totalValoresAcordosNoMes`,
              )?.value,
            )
          "
        />
      </a-col>
    </a-row>

    <a-row :gutter="24" style="margin-top: 24px">
      <a-col :span="12">
        <LineChart
          title="TOTAL ACORDOS"
          :categories="totalAcordosUltimosMesesCategories"
          :series="totalAcordosUltimosMesesSeries"
        />
      </a-col>
      <a-col :span="12">
        <LineChart
          title="PUBLICAÇÕES"
          :categories="totalPublicacoesUltimosDiasCategories"
          :series="totalPublicacoesUltimosDiasSeries"
        />
      </a-col>
    </a-row>

    <a-row :gutter="24" style="margin-top: 24px">
      <a-col :span="12">
        <BarChart
          title="PASTAS POR STATUS"
          :categories="pastasAgrupadasPorStatus.map(([x, _]) => x)"
          :series="[
            {
              name: 'Total de pastas',
              data: pastasAgrupadasPorStatus.map(([_, x]) => x as number),
            },
          ]"
        />
      </a-col>
      <a-col :span="12">
        <BarChart
          title="PASTAS ATIVAS POR RESPONSÁVEL"
          :categories="
            totalPastasAtivasAgrupadosPorResponsavel.map(([x, _]) => x)
          "
          :series="[
            {
              name: 'Total de pastas',
              data: totalPastasAtivasAgrupadosPorResponsavel.map(
                ([_, x]) => x as number,
              ),
            },
          ]"
        />
      </a-col>
    </a-row>
  </div>
</template>
